import React, { Component } from "react";
import PropTypes from "prop-types";
// import classnames from "classnames";
import { DialogTitle, Dialog, LinearProgress } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import withStyles from "@mui/styles/withStyles";
import "./Spinloader.css";

const SpinloaderContext = React.createContext();

export const withSpinloader = (Component) => {
  const WrappedComponent = (props) => (
    <SpinloaderContext.Consumer>
      {(context) => <Component {...props} spinloader={context} />}
    </SpinloaderContext.Consumer>
  );

  WrappedComponent.displayName = `withSpinloader(${
    Component.displayName || Component.name || "Component"
  })`;

  return WrappedComponent;
};

const styles = () => ({
  progressContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  dialog: { pointerEvents: "none" },
  message: {
    display: "inline-block",
    position: "relative",
    top: -10,
    marginLeft: 10,
  },
  linearProgress: {
    height: 10,
  },
  greyedOutBackground: {
    background: "rgba(0,0,0,0.5)",
    position: "fixed",
    inset: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 999999,
  },
});

// this is the main component, it has to be added at the root of the app.
// all components that use withSpinloader(...) will have access to it via this.props.spinloader...
class SpinloaderProvider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rightWidth: 100,
      show: false,
      messagePresent: false,
      message: "",
      indefinite: true,
      progress: 0,
    };
    this.timeout = null;
    this.start_time = performance.now();
  }
  /**
   * Shows an indefinte spinloader
   */
  show() {
    this.start_time = performance.now();
    if (this.state.show) return;
    this.setState(() => ({
      show: true,
      messagePresent: false,
      message: "",
      indefinite: true,
    }));
  }

  /**
   * Shows a progressbar with a prominent message above
   * @param {JSON} progressObject JSON Object with message and progress {message: , progress: }
   */
  showWithProgress = (progressObject) => {
    this.start_time = performance.now();
    const progress = parseInt(progressObject.progress);
    this.setState({
      show: true,
      messagePresent: true,
      message: progressObject.message + ": " + progress + "%",
      progress: isNaN(progress) ? 0 : progress,
      indefinite: false,
    });
    clearTimeout(this.timeout);
  };

  showWithAutoProgress = (message, seconds) => {
    this.start_time = performance.now();
    this.setState(
      {
        show: true,
        messagePresent: true,
        message: message,
        progress: 0,
        indefinite: false,
        currentSecond: 0,
        predictedSeconds: parseInt(seconds),
      },
      () => {
        setTimeout(() => this.updateAutoProgress(), 1000);
      }
    );
  };
  updateAutoProgress = () => {
    if (this.state.show) {
      const progress =
        (100 * this.state.currentSecond) / this.state.predictedSeconds;
      this.setState({
        currentSecond: this.state.currentSecond + 1,
        progress: progress,
      });
      if (progress < 100) {
        setTimeout(() => this.updateAutoProgress(), 1000);
      } else {
        this.showWithMessage(this.state.message);
      }
    }
  };
  /**
   * Shows an indefinite loader with a prominent message above
   * @param {String} message The info to be displayed
   */
  showWithMessage = (message) => {
    this.start_time = performance.now();
    this.setState({
      show: true,
      messagePresent: true,
      message: message,
      indefinite: true,
    });
  };

  /**
   * Shows Spinloader and hides it after a defined time to prevent constant blocking of the screen.
   * @param {int} scenesLeft Scenes to analyse. Used for delay calculation.
   * @param {int} timeoutSeconds Seconds to wait until hiding spinloader
   */
  showWithTimeout(scenesLeft, timeoutSeconds = 30) {
    this.start_time = performance.now();
    let waitTime = 300000;
    if (scenesLeft) {
      waitTime = scenesLeft * timeoutSeconds * 1000;
      waitTime = Math.max(waitTime, 60000);
    }
    this.setState({ show: true });
    this.timeout = setTimeout(() => this.timeoutHide(), waitTime);
  }

  /**
   * Increase the remaining time until hiding spinloader depending on the amount of scenes left.
   * @param {int} scenesLeft Scenes left to analyse. Used for delay calculation.
   * @param {number} timeoutSeconds Optional. Seconds to wait per scene until hiding spinloader. Default is 30 seconds.
   */
  resetTimer(scenesLeft, timeoutSeconds = 30) {
    let waitTime = scenesLeft * timeoutSeconds * 1000;
    waitTime = Math.min(waitTime, 60000);
    clearTimeout(this.timeout);
    if (scenesLeft > 0) {
      this.timeout = setTimeout(() => this.timeoutHide(), waitTime);
    } else {
      this.hide();
    }
  }

  measureTime = () => {
    const executionTime = (performance.now() - this.start_time) / 1000;
    if (executionTime > 10) {
      const minutes = parseInt(executionTime / 60, 10);
      const seconds = parseInt(executionTime % 60, 10);
      const logMessage = `Spinner active time: ${minutes} minutes and ${seconds} seconds!`;
      console.debug(logMessage);
    }
  };

  /**
   * Hides the spinloader.
   */
  hide() {
    this.measureTime();
    clearTimeout(this.timeout);
    this.setState(() => ({ show: false, messagePresent: false, message: "" }));
  }

  timeoutHide() {
    this.hide();
    window.showWarningSnackbar(
      "Warning: Timeout! Process is taking especially long."
    );
  }

  hideTimeDelayed(delay) {
    setTimeout(() => {
      this.hide();
    }, delay);
  }

  load(func) {
    this.setState({ show: true });
    setTimeout(() => {
      func();
      this.setState({ show: false, messagePresent: false });
    }, 0);
  }

  setRightWidth(width) {
    this.setState({ rightWidth: width });
  }

  render() {
    const { classes } = this.props;
    return (
      <SpinloaderContext.Provider
        value={{
          show: () => this.show(),
          showWithTimeout: (numberScenes) => this.showWithTimeout(numberScenes),
          showWithProgress: (progObject) => this.showWithProgress(progObject),
          showWithAutoProgress: (message, seconds) =>
            this.showWithAutoProgress(message, seconds),
          showWithMessage: (message) => this.showWithMessage(message),
          resetTimer: (numberRestScenes) => this.resetTimer(numberRestScenes),
          hide: () => this.hide(),
          hideTimeDelayed: (delay) => this.hideTimeDelayed(delay),
          load: (callback) => this.load(callback),
          setRightWidth: (width) => this.setRightWidth(width),
        }}
      >
        {this.props.children}
        {this.state.show && (
          <div
            className={classes.greyedOutBackground}
            onContextMenu={(e) => e.preventDefault()}
          >
            <div
              className={classes.progressContainer}
              style={{ paddingRight: this.state.rightWidth || 0 }}
            >
              {this.state.messagePresent ? (
                // Dialog option for more text
                <Dialog className={classes.dialog} open={true} hideBackdrop>
                  <DialogTitle>
                    <CircularProgress />
                    <div className={classes.message}>{this.state.message}</div>
                  </DialogTitle>
                  {this.state.indefinite ? (
                    <LinearProgress
                      className={classes.linearProgress}
                      style={{ height: 10 }}
                    />
                  ) : (
                    <LinearProgress
                      variant="determinate"
                      value={this.state.progress}
                      style={{ height: 10 }}
                    />
                  )}
                </Dialog>
              ) : (
                // Normal, indefinite spinloader
                <CircularProgress className={classes.progress} size={50} />
              )}
            </div>
          </div>
        )}
      </SpinloaderContext.Provider>
    );
  }
}

SpinloaderProvider.propTypes = {
  classes: PropTypes.object.isRequired,
  children: PropTypes.element.isRequired,
};

export default withStyles(styles)(SpinloaderProvider);
